import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  TableContainer,
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Icon,
  Checkbox,
  DropDownMenu,
  Paginator,
} from '@safedns/skydns-ui-react-lib';
import styles from './AgentsTable.module.scss';

import AgentTableRow from './AgentsTableRow';
import SearchInput from '../UI/search/SearchInput';
import sprite from '../../icons/sprite.svg';
import Loader from '../UI/loader/Loader';

import { setSearchQuery } from '../../store/agentsReducer';
import usePagination from '../../hooks/usePagination';
import useSortable from '../../hooks/useSortable';
import {
  useGetUserQuery,
  useGetAllAgentsQuery,
  useGetProfilesQuery,
} from '../../services/ADapi';
import { getProfileName } from '../../utils/getProfileName';
import { actionTableCellStyle } from '../../styles/tableStyles';

const filterAgents = (agents, searchQuery = '') => {
  const lowerCaseSearchQuery = searchQuery.toLowerCase();

  if (agents) {
    return agents.filter(({ hostname }) => {
      return hostname.toLowerCase().includes(lowerCaseSearchQuery);
    });
  }

  return [];
};

const setSelectedValues = (selected) => {
  return selected.length > 0 ? `Выбрано ${selected.length}` : 'Все';
};

const DropDownMenuStyles = {
  padding: '0 0 0 8px',
  border: 0,
  height: 'unset',
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
};

const DropDownMenuListStyles = {
  width: 'auto',
  padding: 8,
  overflowY: 'auto',
  transform: 'translateX(70%)',
};

const AgentsTable = () => {
  const { searchQuery } = useSelector((state) => state.agents);

  const {
    data: { user_id },
  } = useGetUserQuery();
  const { data: profiles = [] } = useGetProfilesQuery(user_id);

  const url = `/agents/rest_api/v1/users/${user_id}/agentinfo/?page=1`;

  const { data: allResults, isLoading, error } = useGetAllAgentsQuery(url);

  const [agentOsIds, setAgentOsIds] = useState([]);
  const [selectedOSs, setSelectedOSs] = useState([]);
  const [osCloseTrigger, setOsCloseTrigger] = useState(undefined);

  const [profileIds, setProfileIds] = useState([]);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [profileCloseTrigger, setProfileCloseTrigger] = useState(undefined);

  const agentOSs = useMemo(() => {
    const OSs = new Set();

    allResults?.forEach((agent) => {
      OSs.add(agent.os_info);
    });

    return OSs;
  }, [allResults]);

  let agents = filterAgents(allResults, searchQuery);

  const filteredByProfile = useMemo(() => {
    let result = agents;

    if (selectedProfiles.length === 0 && selectedOSs.length === 0) {
      return agents;
    }

    if (selectedProfiles.length) {
      result = result.filter((agent) =>
        selectedProfiles.includes(agent.profile)
      );
    }

    if (selectedOSs.length) {
      result = result.filter((agent) => selectedOSs.includes(agent.os_info));
    }

    return result;
  }, [agents, selectedProfiles, selectedOSs]);

  const ITEMS_PER_PAGE = 10;
  const {
    firstContentIndex,
    lastContentIndex,
    page,
    gaps,
    totalPages,
    nextPage,
    prevPage,
    setPage,
  } = usePagination({
    contentPerPage: ITEMS_PER_PAGE,
    count: filteredByProfile.length || 0,
  });

  useEffect(() => {
    if (searchQuery.length > 0) {
      setPage(0);
    }
  }, [searchQuery, setPage]);

  let { result, requestSort } = useSortable(filteredByProfile);

  agents = result;

  const lastSeenHandler = () => {
    requestSort('last_seen');
  };

  const handleProfileCheckboxChange = (profileId) => {
    setProfileIds((prev) => {
      if (prev.includes(profileId)) {
        return prev.filter((p) => p !== profileId);
      } else {
        return [...prev, profileId];
      }
    });
  };

  const handleOsCheckboxChange = (osName) => {
    setAgentOsIds((prev) => {
      if (prev.includes(osName)) {
        return prev.filter((o) => o !== osName);
      } else {
        return [...prev, osName];
      }
    });
  };

  const clearSelectedProfiles = () => {
    setProfileIds([]);
    setSelectedProfiles([]);
  };

  const renderFilteredAgentsWithinProfiles = () => {
    setSelectedProfiles(profileIds);
    setProfileCloseTrigger(Date.now());
  };

  const clearSelectedOsIds = () => {
    setAgentOsIds([]);
    setSelectedOSs([]);
  };

  const renderFilteredAgents = () => {
    setSelectedOSs(agentOsIds);
    setOsCloseTrigger(Date.now());
  };

  const handleProfileOpenChange = (open) => {
    if (open) {
      setProfileIds(selectedProfiles);
    }
  };

  const handleOsOpenChange = (open) => {
    if (open) {
      setAgentOsIds(selectedOSs);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <div>Произошла ошибка при загрузке агентов</div>;
  }

  return (
    <>
      <SearchInput
        placeholder="Найти агент"
        cb={setSearchQuery}
        sx={{ maxWidth: 460 }}
      />
      <TableContainer
        className={styles['agents-table-container']}
        sx={{ paddingTop: 24 }}
      >
        <Table sx={{ minWidth: '900px' }}>
          <TableHead>
            <TableRow>
              <TableHeadCell sx={{ textAlign: 'left' }}>
                Устройство
              </TableHeadCell>
              <TableHeadCell sx={{ padding: '16px 0', textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    component="span"
                    sx={{
                      verticalAlign: 'middle',
                      color: 'inherit',
                      fontWeight: 'inherit',
                    }}
                  >
                    Профиль:
                  </Typography>

                  <DropDownMenu
                    id="selectProfile"
                    name={setSelectedValues(selectedProfiles)}
                    closeTrigger={profileCloseTrigger}
                    onOpenChange={handleProfileOpenChange}
                    sx={DropDownMenuStyles}
                    listSx={DropDownMenuListStyles}
                  >
                    {profiles.map((profile) => (
                      <Checkbox
                        key={profile.id}
                        id={profile.id.toString()}
                        label={profile.name}
                        value={profile.name}
                        isChecked={profileIds.includes(profile.id)}
                        sx={{ padding: '4px 8px', textAlign: 'left' }}
                        onChange={() => handleProfileCheckboxChange(profile.id)}
                      />
                    ))}
                    <div className={styles['filter-menu-buttons']}>
                      <Button
                        variant="secondary"
                        onClick={clearSelectedProfiles}
                        className={styles['filter-menu-button']}
                      >
                        Очистить
                      </Button>
                      <Button
                        variant="primary"
                        onClick={renderFilteredAgentsWithinProfiles}
                        className={styles['filter-menu-button']}
                      >
                        Показать
                      </Button>
                    </div>
                  </DropDownMenu>
                </div>
              </TableHeadCell>
              <TableHeadCell sx={{ padding: '16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    component="span"
                    sx={{
                      verticalAlign: 'middle',
                      color: 'inherit',
                      fontWeight: 'inherit',
                    }}
                  >
                    ОС:
                  </Typography>
                  <DropDownMenu
                    name={setSelectedValues(selectedOSs)}
                    closeTrigger={osCloseTrigger}
                    onOpenChange={handleOsOpenChange}
                    sx={DropDownMenuStyles}
                    listSx={DropDownMenuListStyles}
                  >
                    {Array.from(agentOSs).map((os) => (
                      <Checkbox
                        key={os}
                        id={os}
                        label={os}
                        sx={{ padding: '4px 8px', textAlign: 'left' }}
                        isChecked={agentOsIds.includes(os)}
                        onChange={() => handleOsCheckboxChange(os)}
                      />
                    ))}
                    <div className={styles['filter-menu-buttons']}>
                      <Button
                        variant="secondary"
                        onClick={clearSelectedOsIds}
                        className={styles['filter-menu-button']}
                      >
                        Очистить
                      </Button>
                      <Button
                        variant="primary"
                        onClick={renderFilteredAgents}
                        className={styles['filter-menu-button']}
                      >
                        Показать
                      </Button>
                    </div>
                  </DropDownMenu>
                </div>
              </TableHeadCell>
              <TableHeadCell sx={{ padding: '16px 0', textAlign: 'left' }}>
                <Typography
                  component="span"
                  sx={{ color: 'inherit', fontWeight: 'inherit' }}
                >
                  <Button
                    onClick={lastSeenHandler}
                    variant="icon"
                    sx={{
                      marginLeft: 0,
                      padding: 0,
                      verticalAlign: 'middle',
                      color: 'inherit',
                    }}
                  >
                    <Icon
                      id="sort"
                      sprite={sprite}
                      width={18}
                      height={18}
                      viewBox="0 0 18 18"
                    />
                  </Button>
                  <span style={{ verticalAlign: 'middle' }}>
                    Последний визит
                  </span>
                </Typography>
              </TableHeadCell>
              <TableHeadCell sx={actionTableCellStyle}></TableHeadCell>
              <TableHeadCell sx={actionTableCellStyle}></TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredByProfile.length === 0 ? (
              <TableRow>
                <TableCell colSpan="6">
                  <Typography variant="subtitle2" component="p">
                    Нет устройств, соответствующих выбранным фильтрам. Выберите
                    другие позиции.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {agents
              .slice(firstContentIndex, lastContentIndex)
              .map(
                ({
                  id,
                  uid,
                  hostname,
                  comment,
                  profile,
                  os_info,
                  last_seen,
                }) => (
                  <AgentTableRow
                    key={`${id}-agent`}
                    uid={uid}
                    device={hostname}
                    comment={comment}
                    profile={getProfileName(profiles, profile)}
                    os={os_info}
                    lastSeen={last_seen}
                  />
                )
              )}
          </TableBody>
        </Table>
      </TableContainer>
      {agents.length > ITEMS_PER_PAGE ? (
        <Paginator
          nextPage={nextPage}
          prevPage={prevPage}
          page={page}
          gaps={gaps}
          setPage={setPage}
          totalPages={totalPages}
        />
      ) : null}
    </>
  );
};

export default AgentsTable;
